import { LineAxis, StackedLineChart } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { LineChartProps } from "@mui/x-charts/LineChart";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { forceArray } from "../../libs/utils";
import { WorkerQueryOptions } from "../../libs/workers/types";
import { BrushVariationSummary } from "./BrushVariationSummary.component";
import { MuiTrendsChart } from "./MuiTrendsChart.component";
import { RechartsTrendsChart } from "./RechartsTrendsChart.component";
import { DEFAULT_AGGREGATION_PERIODS } from "./TrendOverTimeChart.consts";
import {
  AggregationFn,
  AggregationPeriod,
  AggregationPeriodConfig,
  BrushRange,
  ChartClickEvent,
  ChartSelection,
  ChartVendorProps,
  MetricConfig,
  RechartsDataPoint,
  TrendInput,
  TrendsOverChartEntry,
} from "./TrendsOverTimeChart.types";

// ============================================
// Props Types
// ============================================

interface BaseTrendsOverTimeChartProps {
  title: string;

  timelineData?: TrendsOverChartEntry[] | SharedArrayBuffer | ArrayBuffer;
  metrics: MetricConfig[];
  loading?: boolean;
  width?: number;
  height?: number;
  grid?: { vertical?: boolean; horizontal?: boolean };
  /** Neutral click callback for data points */
  onDataPointClick?: (event: ChartClickEvent) => void;
  /** Neutral click callback for period/axis */
  onPeriodClick?: (event: ChartClickEvent) => void;
  /** @deprecated Use onDataPointClick instead */
  onAreaClick?: (event: any, data: any) => void;
  /** @deprecated Use onPeriodClick instead */
  onAxisClick?: (event: any, data: any) => void;
  onAggregationKeyChange?: (key: string | undefined) => void;
  onMetricsChange?: (metrics: MetricConfig[]) => void;
  onBrushRangeChange?: (range: BrushRange | null) => void;
  brushRange?: BrushRange | null;
  valueFormatter?: (value: number | null) => string | number;
  trends?: [TrendConfigs, TrendConfigs | null | undefined];
  defaultAggregationMode?: "compare" | "stack";
  showAggregationSelector?: boolean;
  showModeSelector?: boolean;
  aggregationKeysOptions?: { value: string; label: string }[];
  defaultAggregationKey?: string;
  defaultAggregationFn?: AggregationFn;
  /** "mono" uses MUI X Charts, "dual" uses Recharts with dual Y axis */
  version?: "mono" | "dual";
  opts?: WorkerQueryOptions;
}

interface DefaultTrendsOverTimeChartProps extends BaseTrendsOverTimeChartProps {
  id?: string;
  defaultAggregationPeriod?: AggregationPeriod;
  aggregationPeriods?: never;
  cacheKey: string;
}

interface CustomTrendsOverTimeChartProps<
  T extends Record<string, AggregationPeriodConfig>
> extends BaseTrendsOverTimeChartProps {
  defaultAggregationPeriod?: keyof T;
  aggregationPeriods: T;
  cacheKey: string;
}

export type TrendConfigs = {
  id: string;
  aggregationKey?: string;
  aggregationFn?: AggregationFn;
  yAxis?: NonNullable<LineChartProps["yAxis"]>[0] & {
    minValue?: number;
    maxValue?: number;
    /** Direction where increase is positive: 'up' = green on increase, 'down' = green on decrease */
    positiveDirection?: "up" | "down";
  };
  aggregationKeysOptions?: { value: string; label: string }[];
  handleAggregationKeyChange: (event: SelectChangeEvent<string>) => void;
  handleAggregationFnChange?: (
    event: React.MouseEvent<HTMLElement>,
    value: AggregationFn
  ) => void;
};

// ============================================
// Helper: Convert TrendConfigs to TrendInput
// ============================================

const toTrendInput = (
  config: TrendConfigs | null | undefined,
  defaultAggregationFn: AggregationFn
): TrendInput | undefined => {
  if (!config) return undefined;
  return {
    id: config.id,
    label: config.label,
    aggregationKey: config.aggregationKey,
    aggregationFn: config.aggregationFn ?? defaultAggregationFn,
    yAxisConfig: config.yAxis
      ? {
          id: String(config.yAxis.id ?? config.id),
          label: config.yAxis.label as string | undefined,
          valueFormatter: config.yAxis.valueFormatter
            ? (value: number | null) => {
                const result = (config.yAxis!.valueFormatter as any)(value);
                return typeof result === "string"
                  ? result
                  : String(result ?? "");
              }
            : undefined,
          minValue: (config.yAxis as any).minValue,
          maxValue: (config.yAxis as any).maxValue,
          positiveDirection: (config.yAxis as any).positiveDirection,
        }
      : undefined,
  };
};

// ============================================
// Main Component
// ============================================

export function TrendsOverTimeChart(
  props: DefaultTrendsOverTimeChartProps
): React.JSX.Element;

export function TrendsOverTimeChart<
  T extends Record<string, AggregationPeriodConfig>
>(props: CustomTrendsOverTimeChartProps<T>): React.JSX.Element;

export function TrendsOverTimeChart<
  T extends Record<string, AggregationPeriodConfig>
>({
  title,
  timelineData,
  metrics: initialMetrics,
  loading = false,
  width = 800,
  height = 400,
  onDataPointClick,
  onPeriodClick,
  onMetricsChange,
  onBrushRangeChange,
  brushRange: controlledBrushRange,
  defaultAggregationKey,
  defaultAggregationPeriod,
  showAggregationSelector = true,
  showModeSelector = true,
  defaultAggregationMode = "compare",
  defaultAggregationFn = "count",
  trends,
  cacheKey,
  grid,
  version = "mono",
  opts,
}: DefaultTrendsOverTimeChartProps | CustomTrendsOverTimeChartProps<T>) {
  // Aggregation period options
  const aggregationPeriods: AggregationPeriod[] = Object.keys(
    DEFAULT_AGGREGATION_PERIODS
  ) as AggregationPeriod[];

  const firstKey = aggregationPeriods[0];
  const initialPeriod = defaultAggregationPeriod || firstKey;

  // State
  const [aggregationPeriod, setAggregationPeriod] = useState<AggregationPeriod>(
    initialPeriod as AggregationPeriod
  );
  const [aggregationMode, setAggregationMode] = useState<"compare" | "stack">(
    defaultAggregationMode
  );
  const [metrics, setMetrics] = useState<MetricConfig[]>(initialMetrics);

  // Brush state for time range selection
  const [internalBrushRange, setInternalBrushRange] = useState<BrushRange | null>(null);
  const brushRange = controlledBrushRange !== undefined ? controlledBrushRange : internalBrushRange;
  const [chartData, setChartData] = useState<RechartsDataPoint[]>([]);

  // Selection state for ReferenceArea drag-to-select
  const [chartSelection, setChartSelection] = useState<ChartSelection | null>(
    null
  );

  const [connectNulls, setConnectNulls] = useState<boolean>(true);

  // Brush change handler
  const handleBrushChange = useCallback((range: BrushRange) => {
    setInternalBrushRange(range);
    onBrushRangeChange?.(range);
  }, [onBrushRangeChange]);

  // Selection change handler
  const handleSelectionChange = useCallback(
    (selection: ChartSelection | null) => {
      setChartSelection(selection);
    },
    []
  );

  // Chart data ready handler
  const handleChartDataReady = useCallback((data: RechartsDataPoint[]) => {
    setChartData(data);
  }, []);

  // Sync metrics when initialMetrics changes
  useEffect(() => {
    setMetrics(initialMetrics);
  }, [initialMetrics]);

  // Filter enabled metrics
  const enabledMetrics = useMemo(
    () => metrics.filter((metric) => metric.enabled),
    [metrics]
  );

  // Extract trends
  const comparand = useMemo(() => trends?.[0], [trends]);
  const comparator = useMemo(() => trends?.[1], [trends]);

  // Convert TrendConfigs to TrendInput for vendor components
  const comparandInput = useMemo(
    () => toTrendInput(comparand, defaultAggregationFn),
    [comparand, defaultAggregationFn]
  );

  const comparatorInput = useMemo(
    () => toTrendInput(comparator, defaultAggregationFn),
    [comparator, defaultAggregationFn]
  );

  // Period selector options
  const aggregationPeriodOptions = useMemo(
    () =>
      aggregationPeriods.map((key: AggregationPeriod) => ({
        value: key as string,
        label: DEFAULT_AGGREGATION_PERIODS[key].label,
      })),
    [aggregationPeriods]
  );

  // Handlers
  const handleAggregationPeriodChange = (event: SelectChangeEvent<string>) => {
    setAggregationPeriod(event.target.value as AggregationPeriod);
  };

  const handleMetricToggle = (metricKey: string) => {
    setMetrics((prev) => {
      const updated = prev.map((metric) =>
        String(metric.key) === String(metricKey)
          ? { ...metric, enabled: !metric.enabled }
          : metric
      );
      onMetricsChange?.(updated);
      return updated;
    });
  };

  const handleModeChange = (newMode: "compare" | "stack") => {
    setAggregationMode(newMode);
    setMetrics((prev) =>
      prev.map((metric) =>
        metric.key === "all"
          ? metric
          : {
              ...metric,
              area: newMode === "stack",
              stack: newMode === "stack" ? "default" : undefined,
            }
      )
    );
  };

  const vendorProps = useMemo(() => {
    return {
      cacheKey,
      entries: timelineData,
      metrics: enabledMetrics,
      aggregationPeriod,
      aggregationMode,
      defaultAggregationFn,
      width,
      height,
      grid,
      loading,
      opts,
      onDataPointClick,
      onPeriodClick,
      // Brush props - enabled for zoom/navigation
      brushEnabled: version === "dual",
      brushHeight: 40,
      brushRange: brushRange,
      onBrushChange: handleBrushChange,
      // Selection props - enabled for variation analysis
      selectionEnabled: version === "dual",
      onSelectionChange: handleSelectionChange,
      onChartDataReady: handleChartDataReady,
    } as ChartVendorProps;
  }, [
    cacheKey,
    enabledMetrics,
    aggregationPeriod,
    aggregationMode,
    defaultAggregationFn,
    width,
    height,
    grid,
    loading,
    opts,
    onDataPointClick,
    onPeriodClick,
    timelineData,
    version,
    brushRange,
    handleBrushChange,
    handleSelectionChange,
    handleChartDataReady,
  ]);

  // Card header action (selectors)
  const cardAction = useMemo(
    () => (
      <Stack direction="row" spacing={2} alignItems="center">
        <ToggleButton
          size="small"
          value="connectNulls"
          selected={connectNulls}
          onChange={() => setConnectNulls(!connectNulls)}
        >
          Connetti punti nulli
        </ToggleButton>
        <ModeSelector
          show={showModeSelector}
          mode={aggregationMode}
          onModeChange={handleModeChange}
        />
        <AggregationSelector
          show={showAggregationSelector}
          defaultKey={aggregationPeriod}
          onChange={handleAggregationPeriodChange}
          options={aggregationPeriodOptions}
        />
      </Stack>
    ),
    [
      aggregationMode,
      aggregationPeriod,
      showModeSelector,
      showAggregationSelector,
      aggregationPeriodOptions,
    ]
  );

  return (
    <Card>
      <CardHeader title={title} action={cardAction} />
      <CardContent>
        <Stack spacing={2}>
          {/* Trend configurators */}
          {version === "dual" && (
            <Stack direction="row" spacing={16} justifyContent={"center"}>
              <TrendConfigurator
                label="Valore"
                defaultAggregationKey={comparand?.aggregationKey}
                defaultAggregationFn={comparand?.aggregationFn}
                aggregationKeysOptions={comparand?.aggregationKeysOptions}
                handleAggregationKeyChange={
                  comparand?.handleAggregationKeyChange
                }
                handleAggregationFnChange={comparand?.handleAggregationFnChange}
              />
              <TrendConfigurator
                label="Compara con"
                defaultAggregationKey={comparator?.aggregationKey}
                defaultAggregationFn={comparator?.aggregationFn}
                aggregationKeysOptions={comparator?.aggregationKeysOptions}
                handleAggregationKeyChange={
                  comparator?.handleAggregationKeyChange
                }
                handleAggregationFnChange={
                  comparator?.handleAggregationFnChange
                }
              />
            </Stack>
          )}
          {/* Chart + Metrics sidebar */}
          <Stack direction="row" spacing={2}>
            {/* Chart area */}
            <Box flex={1}>
              {vendorProps && version === "mono" && (
                <MuiTrendsChart {...vendorProps} />
              )}
              {vendorProps && version === "dual" && comparandInput && (
                <RechartsTrendsChart
                  {...vendorProps}
                  comparand={comparandInput}
                  comparator={comparatorInput}
                  connectNulls={connectNulls}
                />
              )}
            </Box>

            {/* Metrics sidebar */}
            <MetricsSidebar
              metrics={metrics}
              height={height}
              onToggle={handleMetricToggle}
              onToggleAll={() => {
                const allEnabled = metrics.every((m) => m.enabled);
                setMetrics((prev) => {
                  const updated = prev.map((m) => ({ ...m, enabled: !allEnabled }));
                  onMetricsChange?.(updated);
                  return updated;
                });
              }}
            />
          </Stack>

          {/* Selection variation summary */}
          {version === "dual" && chartData.length > 0 && comparandInput && (
            <BrushVariationSummary
              selection={chartSelection}
              data={chartData}
              metrics={enabledMetrics}
              comparandYAxisId={comparandInput.id}
              comparatorYAxisId={comparatorInput?.id}
              comparandValueFormatter={
                comparandInput.yAxisConfig?.valueFormatter
              }
              comparatorValueFormatter={
                comparatorInput?.yAxisConfig?.valueFormatter
              }
              comparandPositiveDirection={
                comparandInput.yAxisConfig?.positiveDirection
              }
              comparatorPositiveDirection={
                comparatorInput?.yAxisConfig?.positiveDirection
              }
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

// ============================================
// Sub-components
// ============================================

function AggregationSelector({
  label,
  show,
  defaultKey,
  onChange,
  options,
}: {
  label?: string;
  show: boolean;
  defaultKey?: string;
  onChange?: (event: SelectChangeEvent<string>) => void;
  options: { value: string | undefined; label: string }[];
}) {
  if (!show || options.length <= 1) return null;

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <FormControl variant="filled" size="small">
        <InputLabel id="aggregation-period-label">
          {label || "Periodo di aggregazione"}
        </InputLabel>
        <Select
          labelId="aggregation-period-label"
          value={defaultKey}
          onChange={onChange}
          sx={{ minWidth: 170 }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
}

function AggregationFnSelector({
  show,
  defaultValue,
  onChange,
}: {
  show: boolean;
  defaultValue?: AggregationFn;
  onChange?: (
    event: React.MouseEvent<HTMLElement>,
    value: AggregationFn
  ) => void;
}) {
  if (!show) return null;

  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      onChange={onChange}
      value={defaultValue}
    >
      <Tooltip title="Somma i valori in ogni periodo">
        <ToggleButton value="sum">Somma</ToggleButton>
      </Tooltip>
      <Tooltip title="Conta il numero di record in ogni periodo">
        <ToggleButton value="count">Conta</ToggleButton>
      </Tooltip>
      <Tooltip title="Calcola la media dei valori in ogni periodo">
        <ToggleButton value="average">Media</ToggleButton>
      </Tooltip>
    </ToggleButtonGroup>
  );
}

function TrendConfigurator({
  label,
  defaultAggregationKey,
  defaultAggregationFn,
  aggregationKeysOptions,
  handleAggregationKeyChange,
  handleAggregationFnChange,
}: {
  label?: string;
  defaultAggregationKey?: string;
  defaultAggregationFn?: AggregationFn;
  handleAggregationKeyChange?: (event: SelectChangeEvent<string>) => void;
  handleAggregationFnChange?: (
    event: React.MouseEvent<HTMLElement>,
    value: AggregationFn
  ) => void;
  aggregationKeysOptions?: { value: string; label: string }[];
}) {
  const options: { value: string | undefined; label: string }[] =
    useMemo(() => {
      return (
        [{ value: undefined, label: "--" }] as {
          value: string | undefined;
          label: string;
        }[]
      ).concat(forceArray(aggregationKeysOptions));
    }, [aggregationKeysOptions]);

  return (
    <Stack direction="row" spacing={1}>
      {aggregationKeysOptions && (
        <AggregationSelector
          label={label}
          show={Boolean(aggregationKeysOptions?.length)}
          defaultKey={defaultAggregationKey}
          onChange={handleAggregationKeyChange}
          options={options}
        />
      )}
      <AggregationFnSelector
        show={true}
        defaultValue={defaultAggregationFn}
        onChange={handleAggregationFnChange}
      />
    </Stack>
  );
}

function ModeSelector({
  show,
  mode,
  onModeChange,
}: {
  show: boolean;
  mode: "compare" | "stack";
  onModeChange: (mode: "compare" | "stack") => void;
}) {
  if (!show) return null;

  return (
    <Stack direction="column" justifyContent="center">
      <Typography variant="caption">Visualizza:</Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={mode}
        onChange={(_, value) => {
          if (value) onModeChange(value);
        }}
      >
        <Tooltip title="Confronto">
          <ToggleButton value="compare">
            <LineAxis />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Cumulativo">
          <ToggleButton value="stack">
            <StackedLineChart />
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>
    </Stack>
  );
}

function MetricsSidebar({
  metrics,
  height,
  onToggle,
  onToggleAll,
}: {
  metrics: MetricConfig[];
  height: number;
  onToggle: (key: string) => void;
  onToggleAll: () => void;
}) {
  const theme = useTheme();
  const allEnabled = metrics.every((m) => m.enabled);
  const someEnabled = metrics.some((m) => m.enabled);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        maxHeight: height,
        overflowY: "auto",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" gutterBottom>
          Metriche visibili:
        </Typography>
      </Stack>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={allEnabled}
              indeterminate={someEnabled && !allEnabled}
              onChange={onToggleAll}
              size="small"
            />
          }
          label={!allEnabled ? "Mostra Tutto" : "Nascondi Tutto"}
          sx={{
            position: "sticky",
            top: 0,
            backgroundColor: theme.palette.background.paper,
            zIndex: 1,
          }}
          slotProps={{ typography: { fontSize: "0.85rem" } }}
        />
        <Divider />
        {metrics.map((metric) => (
          <FormControlLabel
            key={metric.key}
            control={
              <Checkbox
                checked={metric.enabled}
                onChange={() => onToggle(metric.key)}
                sx={{
                  color: metric.color,
                  "&.Mui-checked": { color: metric.color },
                }}
              />
            }
            label={metric.label}
          />
        ))}
      </FormGroup>
    </Box>
  );
}
